const DEFAULT_FALLBACK_MESSAGE = "Falha ao processar a solicitacao.";
const ERROR_CODE_PATTERN = /\bERRO-\d+\b/i;

const PRIORITY_PATHS = [
    ["response", "data", "message"],
    ["response", "data", "errors"],
    ["response", "data", "data", "message"],
    ["response", "data", "data", "errors"],
    ["data", "message"],
    ["data", "errors"],
    ["message"],
    ["errors"],
];

const OBJECT_KEYS_PRIORITY = [
    "message",
    "messages",
    "errors",
    "error",
    "detail",
    "details",
    "informacao",
];

const isPlainObject = (value) =>
    value !== null && typeof value === "object" && !Array.isArray(value);

const normalizeText = (value) =>
    String(value || "")
        .replace(/\s+/g, " ")
        .trim();

const getValueAtPath = (source, path) =>
    path.reduce(
        (currentValue, currentKey) =>
            currentValue != null ? currentValue[currentKey] : undefined,
        source
    );

const uniqueMessages = (messages) => {
    const seen = new Set();

    return messages.filter((message) => {
        const normalizedMessage = normalizeText(message);

        if (!normalizedMessage || seen.has(normalizedMessage)) {
            return false;
        }

        seen.add(normalizedMessage);
        return true;
    });
};

const extractTextFromValue = (value, visited = new WeakSet()) => {
    if (value == null) {
        return "";
    }

    if (typeof value === "string" || typeof value === "number") {
        return normalizeText(value);
    }

    if (typeof value === "boolean") {
        return value ? "true" : "false";
    }

    if (Array.isArray(value)) {
        return uniqueMessages(
            value
                .map((item) => extractTextFromValue(item, visited))
                .filter(Boolean)
        ).join(" | ");
    }

    if (!isPlainObject(value)) {
        return "";
    }

    if (visited.has(value)) {
        return "";
    }

    visited.add(value);

    const prioritizedMessages = OBJECT_KEYS_PRIORITY
        .map((key) => extractTextFromValue(value[key], visited))
        .filter(Boolean);

    if (prioritizedMessages.length > 0) {
        return uniqueMessages(prioritizedMessages).join(" | ");
    }

    const nestedMessages = Object.values(value)
        .map((item) => extractTextFromValue(item, visited))
        .filter(Boolean);

    return uniqueMessages(nestedMessages).join(" | ");
};

const collectNestedCandidates = (
    value,
    path = [],
    visited = new WeakSet(),
    candidates = []
) => {
    if (value == null) {
        return candidates;
    }

    const message = extractTextFromValue(value);
    if (message) {
        candidates.push({
            path: path.join("."),
            message,
        });
    }

    if (typeof value !== "object" || visited.has(value)) {
        return candidates;
    }

    visited.add(value);

    if (Array.isArray(value)) {
        value.forEach((item, index) => {
            collectNestedCandidates(item, [...path, index], visited, candidates);
        });

        return candidates;
    }

    OBJECT_KEYS_PRIORITY.forEach((key) => {
        if (value[key] !== undefined) {
            collectNestedCandidates(value[key], [...path, key], visited, candidates);
        }
    });

    Object.keys(value).forEach((key) => {
        if (!OBJECT_KEYS_PRIORITY.includes(key)) {
            collectNestedCandidates(value[key], [...path, key], visited, candidates);
        }
    });

    return candidates;
};

const buildPriorityCandidates = (error) =>
    PRIORITY_PATHS.map((path) => ({
        path: path.join("."),
        message: extractTextFromValue(getValueAtPath(error, path)),
    })).filter((candidate) => candidate.message);

const scoreCandidate = (candidate, index) => {
    const normalizedPath = candidate.path || "";
    const hasErrorCode = ERROR_CODE_PATTERN.test(candidate.message);
    const isMessagePath = /(^|\.)(message|messages)(\.|$)/.test(normalizedPath);
    const isErrorsPath = /(^|\.)(errors|error)(\.|$)/.test(normalizedPath);

    return [
        hasErrorCode ? 3 : 0,
        isMessagePath ? 2 : 0,
        isErrorsPath ? 1 : 0,
        index * -1,
    ];
};

const compareScores = (currentScore, bestScore) => {
    for (let index = 0; index < currentScore.length; index += 1) {
        if (currentScore[index] !== bestScore[index]) {
            return currentScore[index] - bestScore[index];
        }
    }

    return 0;
};

export const identifyErrorMenssageDetails = (
    error,
    fallbackMessage = DEFAULT_FALLBACK_MESSAGE
) => {
    const candidates = [
        ...buildPriorityCandidates(error),
        ...collectNestedCandidates(error),
    ];

    const dedupedCandidates = uniqueMessages(candidates.map((candidate) => candidate.message))
        .map((message) => candidates.find((candidate) => candidate.message === message))
        .filter(Boolean);

    const candidatesWithErrorCode = dedupedCandidates.filter((candidate) =>
        ERROR_CODE_PATTERN.test(candidate.message)
    );

    if (candidatesWithErrorCode.length === 0) {
        return {
            path: null,
            message: fallbackMessage,
        };
    }

    let bestCandidate = candidatesWithErrorCode[0];
    let bestScore = scoreCandidate(bestCandidate, 0);

    candidatesWithErrorCode.forEach((candidate, index) => {
        const currentScore = scoreCandidate(candidate, index);

        if (compareScores(currentScore, bestScore) > 0) {
            bestCandidate = candidate;
            bestScore = currentScore;
        }
    });

    return {
        path: bestCandidate.path || null,
        message: bestCandidate.message || fallbackMessage,
    };
};

export const identifyErrorMenssage = (
    error,
    fallbackMessage = DEFAULT_FALLBACK_MESSAGE
) => identifyErrorMenssageDetails(error, fallbackMessage).message;

export default identifyErrorMenssage;
